import { useState, useEffect, useRef } from "react";

interface TypewriterOptions {
  /**
   * Delay between each character in milliseconds
   * @default 100
   */
  delay?: number;
  /**
   * Number of characters to render per tick
   * @default 1
   */
  chunkSize?: number;
  /**
   * Whether to loop the animation
   * @default false
   */
  loop?: boolean;
  /**
   * Initial delay before starting the animation in milliseconds
   * @default 0
   */
  startDelay?: number;
  /**
   * Whether to disable the typewriter animation effect
   * @default false
   */
  disableAnimation?: boolean;
}

interface TypewriterResult {
  /**
   * The current displayed text
   */
  displayText: string;
  /**
   * Whether the typing animation is complete
   */
  isComplete: boolean;
  /**
   * Function to start the animation over
   */
  restart: () => void;
}

const useTypewriterEffect = (
  text: string,
  options: TypewriterOptions = {}
): TypewriterResult => {
  const {
    delay = 20,
    chunkSize = 3,
    loop = false,
    startDelay = 0,
    disableAnimation = false,
  } = options;

  const [displayText, setDisplayText] = useState<string>(
    disableAnimation ? text : ""
  );
  const [isComplete, setIsComplete] = useState<boolean>(disableAnimation);
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  // Keep track of the previous text to detect changes
  const previousText = useRef<string>("");
  const disableAnimationRef = useRef<boolean>(disableAnimation);

  useEffect(() => {
    // Reflect changes to disableAnimation dynamically
    if (disableAnimation !== disableAnimationRef.current) {
      disableAnimationRef.current = disableAnimation;

      if (disableAnimation) {
        setDisplayText(text);
        setIsComplete(true);
      } else {
        setDisplayText("");
        setCurrentIndex(0);
        setIsComplete(false);
      }
    }
  }, [disableAnimation, text]);

  useEffect(() => {
    if (disableAnimation) {
      return;
    }

    if (text === previousText.current) {
      return;
    }

    // If the new text starts with the current display text, continue typing
    if (typeof text === "string" && text?.startsWith(displayText)) {
      setIsComplete(false);
    } else {
      // Reset if the new text is completely different
      setDisplayText("");
      setCurrentIndex(0);
      setIsComplete(false);
    }

    previousText.current = text;
  }, [text, disableAnimation, displayText]);

  useEffect(() => {
    if (disableAnimation) {
      return;
    }

    if (currentIndex >= text.length) {
      setIsComplete(true);
      if (loop) {
        setTimeout(() => {
          setDisplayText("");
          setCurrentIndex(0);
          setIsComplete(false);
        }, delay * 3); // Add a delay before looping
      }
      return;
    }

    // Schedule the next characters to be displayed.
    // Important: derive displayText from `text.slice(0, nextIndex)` to keep state consistent
    // even when `chunkSize > 1` and `text` is updated frequently by polling.
    const timeoutId = setTimeout(() => {
      const nextIndex = Math.min(currentIndex + chunkSize, text.length);
      setDisplayText(text.slice(0, nextIndex));
      setCurrentIndex(nextIndex);
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [currentIndex, delay, text, disableAnimation, loop, chunkSize]);

  const restart = () => {
    if (disableAnimation) {
      setDisplayText(text);
      setIsComplete(true);
      return;
    }

    setDisplayText("");
    setCurrentIndex(0);
    setIsComplete(false);
  };

  return { displayText, isComplete, restart };
};

export default useTypewriterEffect;
